import React, { createContext, useContext, useState, ReactNode } from 'react';

export type Language = 'en' | 'zh' | 'fr';

interface TranslationContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

// Translation data
const translations = {
  en: {
    // Header
    'header.home': 'HOME',
    'header.rewards': 'REWARDS',
    'header.subscribe': 'SUBSCRIBE',
    'header.profile': 'PROFILE',
    'header.news': 'NEWS',
    'header.live': 'LIVE',
    'header.videos': 'VIDEOS',
    
    // Login Modal
    'login.welcome': 'Welcome to GlobalSport',
    'login.title': 'LOGIN',
    'login.signup': 'SIGN UP',
    'login.subtitle': 'Join millions of users worldwide',
    'login.phone.label': 'Phone Number',
    'login.phone.placeholder': '+91 9876543210',
    'login.terms': 'I agree to the',
    'login.terms.link': 'Terms',
    'login.privacy.link': 'Privacy Policy',
    'login.and': 'and',
    'login.send.otp': 'Send OTP',
    'login.continue.with': 'or continue with',
    'login.google': 'Google',
    'login.apple': 'Apple',
    'login.email': 'Email',
    'login.features.gaming': 'Gaming Content',
    'login.features.mobile': 'Mobile First',
    'login.features.streaming': 'Fast Streaming',
    'login.security': 'Your data is encrypted and secure',
    'login.footer.text': 'New to GlobalSport?',
    'login.footer.highlight': 'Get started for free!',
    'login.stats.users': '10M+ Users',
    'login.stats.uptime': '99.9% Uptime',
    
    // OTP Modal
    'otp.title': 'Verify Your Phone',
    'otp.subtitle': 'Almost there! Complete your verification',
    'otp.description': 'Enter the 4-digit code sent to',
    'otp.features.secure': 'Secure Login',
    'otp.features.quick': 'Quick Setup',
    'otp.features.protected': 'Protected Account',
    'otp.security': 'Your verification is secure and encrypted',
    'otp.resend.timer': 'Resend Available in',
    'otp.resend.description': "Didn't receive the code?",
    'otp.resend.button': 'Resend OTP',
    'otp.verify.button': 'Verify & Continue',
    'otp.tips.check': 'Check your messages for the code',
    'otp.tips.expires': 'Code expires in 2 minutes',
    
    // Subscription Page
    'subscription.title': 'Choose Your Plan',
    'subscription.subtitle': 'Unlock premium features with our flexible plans',
    'subscription.daily': 'Daily Plan',
    'subscription.weekly': 'Weekly Plan',
    'subscription.monthly': 'Monthly Plan',
    'subscription.popular': 'Most Popular',
    'subscription.price': '₹{price}',
    'subscription.features.unlimited': 'Unlimited Streaming',
    'subscription.features.hd': 'HD & 4K Quality',
    'subscription.features.devices': 'Multiple Devices',
    'subscription.features.offline': 'Offline Downloads',
    'subscription.features.ads': 'Ad-free Experience',
    'subscription.features.support': 'Priority Support',
    'subscription.payment.title': 'Select Payment Method',
    'subscription.payment.wallet': 'Wallet',
    'subscription.payment.bank': 'Bank Transfer',
    'subscription.coupon': 'Have a coupon code?',
    'subscription.coupon.placeholder': 'Enter coupon code',
    'subscription.coupon.apply': 'Apply',
    'subscription.pay.button': 'Pay Now',
    
    // Profile Menu
    'profile.menu.profile': 'PROFILE',
    'profile.menu.subscriptions': 'SUBSCRIPTIONS',
    'profile.menu.help': 'HELP',
    'profile.menu.about': 'ABOUT',
    'profile.menu.faq': 'FAQ',
    'profile.menu.logout': 'LOGOUT',
    
    // Subscription Management
    'subscription.management.title': 'Subscription Management',
    'subscription.management.subtitle': 'Manage your GlobalSport subscription and billing',
    'subscription.management.back': 'Back to Profile',
    'subscription.management.plan': 'Premium Monthly',
    'subscription.management.status': 'Active',
    'subscription.management.start.date': 'Start Date',
    'subscription.management.next.billing': 'Next Billing',
    'subscription.management.billing.cycle': 'Billing Cycle',
    'subscription.management.auto.renewal': 'Auto Renewal',
    'subscription.management.upgrade': 'Upgrade Plan',
    'subscription.management.cancel': 'Cancel Subscription',
    'subscription.management.features.title': 'Your Plan Features',
    'subscription.management.usage.title': 'Usage Statistics',
    'subscription.management.usage.devices': 'Active Devices',
    'subscription.management.usage.downloads': 'Downloads',
    'subscription.management.usage.streaming': 'Streaming Hours',
    'subscription.management.billing.title': 'Billing History',
    'subscription.management.payment.title': 'Payment Method',
    'subscription.management.payment.update': 'Update',
    
    // Unsubscribe Page
    'unsubscribe.title': "We're Sorry to See You Go!",
    'unsubscribe.subtitle': "Help us improve by telling us why you're unsubscribing",
    'unsubscribe.reason.expensive': 'Too expensive',
    'unsubscribe.reason.not.using': 'Not using the service',
    'unsubscribe.reason.technical': 'Technical issues',
    'unsubscribe.reason.alternative': 'Found a better alternative',
    'unsubscribe.reason.content': 'Content not interesting',
    'unsubscribe.reason.emails': 'Too many emails/notifications',
    'unsubscribe.reason.temporary': 'Taking a break (temporary)',
    'unsubscribe.reason.other': 'Other reason',
    'unsubscribe.feedback': 'Additional Feedback (Optional)',
    'unsubscribe.feedback.placeholder': 'Tell us more about your experience or suggestions for improvement...',
    'unsubscribe.warning.title': 'Important Information',
    'unsubscribe.warning.billing': 'Your subscription will end at the end of the current billing period',
    'unsubscribe.warning.access': "You'll retain access until then",
    'unsubscribe.warning.refund': 'No refunds will be issued for the current period',
    'unsubscribe.warning.resubscribe': 'You can resubscribe anytime',
    'unsubscribe.confirm': 'Confirm Unsubscribe',
    'unsubscribe.stay': 'Stay Subscribed',
    'unsubscribe.success.title': 'Successfully Unsubscribed',
    'unsubscribe.success.message': "We're sorry to see you go! Your subscription has been cancelled and you won't be charged again.",
    'unsubscribe.success.logout': 'You will be automatically logged out in {count} seconds.',
    'unsubscribe.success.continue': 'Continue to Home',
    'unsubscribe.success.resubscribe': 'Resubscribe',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'common.close': 'Close',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.save': 'Save',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.yes': 'Yes',
    'common.no': 'No',
    
    // Homepage Content
    'homepage.carousel.title': 'Discover Amazing Videos',
    'homepage.carousel.subtitle': 'Watch the latest and greatest videos',
    'homepage.categories.title': 'Video Categories',
    'homepage.categories.subtitle': 'Explore videos by category',
    'homepage.explore.title': 'Explore More',
    'homepage.explore.subtitle': 'Find your next favorite video',
    'homepage.videos.title': 'Featured Videos',
    'homepage.videos.subtitle': 'Watch videos and reviews',
    'homepage.action.play': 'Watch Now',
    'homepage.action.watch': 'Watch',
    'homepage.action.explore': 'Explore',
    'homepage.action.moreInfo': 'More Info',
  },
  
  zh: {
    // Header
    'header.home': '首页',
    'header.rewards': '奖励',
    'header.subscribe': '订阅',
    'header.profile': '个人资料',
    'header.news': '新闻',
    'header.live': '直播',
    'header.videos': '视频',
    
    // Login Modal
    'login.welcome': '欢迎来到 GlobalSport',
    'login.title': '登录',
    'login.signup': '注册',
    'login.subtitle': '加入全球数百万用户',
    'login.phone.label': '手机号码',
    'login.phone.placeholder': '+91 9876543210',
    'login.terms': '我同意',
    'login.terms.link': '条款',
    'login.privacy.link': '隐私政策',
    'login.and': '和',
    'login.send.otp': '发送验证码',
    'login.continue.with': '或使用以下方式继续',
    'login.google': '谷歌',
    'login.apple': '苹果',
    'login.email': '邮箱',
    'login.features.gaming': '游戏内容',
    'login.features.mobile': '移动优先',
    'login.features.streaming': '快速流媒体',
    'login.security': '您的数据已加密并安全',
    'login.footer.text': '新用户？',
    'login.footer.highlight': '免费开始！',
    'login.stats.users': '1000万+ 用户',
    'login.stats.uptime': '99.9% 正常运行时间',
    
    // OTP Modal
    'otp.title': '验证您的手机',
    'otp.subtitle': '快完成了！完成您的验证',
    'otp.description': '输入发送到以下号码的4位验证码',
    'otp.features.secure': '安全登录',
    'otp.features.quick': '快速设置',
    'otp.features.protected': '受保护的账户',
    'otp.security': '您的验证已加密并安全',
    'otp.resend.timer': '重新发送将在',
    'otp.resend.description': '没有收到验证码？',
    'otp.resend.button': '重新发送验证码',
    'otp.verify.button': '验证并继续',
    'otp.tips.check': '检查您的消息获取验证码',
    'otp.tips.expires': '验证码在2分钟内过期',
    
    // Subscription Page
    'subscription.title': '选择您的套餐',
    'subscription.subtitle': '通过我们的灵活套餐解锁高级功能',
    'subscription.daily': '日套餐',
    'subscription.weekly': '周套餐',
    'subscription.monthly': '月套餐',
    'subscription.popular': '最受欢迎',
    'subscription.price': '₹{price}',
    'subscription.features.unlimited': '无限流媒体',
    'subscription.features.hd': '高清和4K质量',
    'subscription.features.devices': '多设备',
    'subscription.features.offline': '离线下载',
    'subscription.features.ads': '无广告体验',
    'subscription.features.support': '优先支持',
    'subscription.payment.title': '选择支付方式',
    'subscription.payment.wallet': '钱包',
    'subscription.payment.bank': '银行转账',
    'subscription.coupon': '有优惠券代码？',
    'subscription.coupon.placeholder': '输入优惠券代码',
    'subscription.coupon.apply': '应用',
    'subscription.pay.button': '立即支付',
    
    // Profile Menu
    'profile.menu.profile': '个人资料',
    'profile.menu.subscriptions': '订阅',
    'profile.menu.help': '帮助',
    'profile.menu.about': '关于',
    'profile.menu.faq': '常见问题',
    'profile.menu.logout': '退出登录',
    
    // Subscription Management
    'subscription.management.title': '订阅管理',
    'subscription.management.subtitle': '管理您的 GlobalSport 订阅和账单',
    'subscription.management.back': '返回个人资料',
    'subscription.management.plan': '高级月套餐',
    'subscription.management.status': '活跃',
    'subscription.management.start.date': '开始日期',
    'subscription.management.next.billing': '下次账单',
    'subscription.management.billing.cycle': '账单周期',
    'subscription.management.auto.renewal': '自动续费',
    'subscription.management.upgrade': '升级套餐',
    'subscription.management.cancel': '取消订阅',
    'subscription.management.features.title': '您的套餐功能',
    'subscription.management.usage.title': '使用统计',
    'subscription.management.usage.devices': '活跃设备',
    'subscription.management.usage.downloads': '下载',
    'subscription.management.usage.streaming': '流媒体时长',
    'subscription.management.billing.title': '账单历史',
    'subscription.management.payment.title': '支付方式',
    'subscription.management.payment.update': '更新',
    
    // Unsubscribe Page
    'unsubscribe.title': '很遗憾看到您离开！',
    'unsubscribe.subtitle': '请告诉我们您取消订阅的原因，帮助我们改进',
    'unsubscribe.reason.expensive': '太贵了',
    'unsubscribe.reason.not.using': '没有使用服务',
    'unsubscribe.reason.technical': '技术问题',
    'unsubscribe.reason.alternative': '找到了更好的替代品',
    'unsubscribe.reason.content': '内容不感兴趣',
    'unsubscribe.reason.emails': '邮件/通知太多',
    'unsubscribe.reason.temporary': '暂时休息',
    'unsubscribe.reason.other': '其他原因',
    'unsubscribe.feedback': '其他反馈（可选）',
    'unsubscribe.feedback.placeholder': '告诉我们更多关于您的体验或改进建议...',
    'unsubscribe.warning.title': '重要信息',
    'unsubscribe.warning.billing': '您的订阅将在当前账单周期结束时结束',
    'unsubscribe.warning.access': '您将保留访问权限直到那时',
    'unsubscribe.warning.refund': '当前周期不会退款',
    'unsubscribe.warning.resubscribe': '您可以随时重新订阅',
    'unsubscribe.confirm': '确认取消订阅',
    'unsubscribe.stay': '保持订阅',
    'unsubscribe.success.title': '成功取消订阅',
    'unsubscribe.success.message': '很遗憾看到您离开！您的订阅已被取消，不会再收费。',
    'unsubscribe.success.logout': '您将在 {count} 秒后自动退出登录。',
    'unsubscribe.success.continue': '继续到首页',
    'unsubscribe.success.resubscribe': '重新订阅',
    
    // Common
    'common.loading': '加载中...',
    'common.error': '错误',
    'common.success': '成功',
    'common.cancel': '取消',
    'common.confirm': '确认',
    'common.close': '关闭',
    'common.back': '返回',
    'common.next': '下一步',
    'common.previous': '上一步',
    'common.save': '保存',
    'common.edit': '编辑',
    'common.delete': '删除',
    'common.yes': '是',
    'common.no': '否',
    
    // Homepage Content
    'homepage.carousel.title': '发现精彩视频',
    'homepage.carousel.subtitle': '观看最新的精彩视频',
    'homepage.categories.title': '视频分类',
    'homepage.categories.subtitle': '按分类探索视频',
    'homepage.explore.title': '探索更多',
    'homepage.explore.subtitle': '找到您下一个喜爱的视频',
    'homepage.videos.title': '精选视频',
    'homepage.videos.subtitle': '观看视频和评论',
    'homepage.action.play': '立即观看',
    'homepage.action.watch': '观看',
    'homepage.action.explore': '探索',
    'homepage.action.moreInfo': '更多信息',
  },
  
  fr: {
    // Header
    'header.home': 'ACCUEIL',
    'header.rewards': 'RÉCOMPENSES',
    'header.subscribe': 'S\'ABONNER',
    'header.profile': 'PROFIL',
    'header.news': 'ACTUALITÉS',
    'header.live': 'EN DIRECT',
    'header.videos': 'VIDÉOS',
    
    // Login Modal
    'login.welcome': 'Bienvenue sur GlobalSport',
    'login.title': 'CONNEXION',
    'login.signup': 'S\'INSCRIRE',
    'login.subtitle': 'Rejoignez des millions d\'utilisateurs dans le monde',
    'login.phone.label': 'Numéro de téléphone',
    'login.phone.placeholder': '+91 9876543210',
    'login.terms': 'J\'accepte les',
    'login.terms.link': 'Conditions',
    'login.privacy.link': 'Politique de confidentialité',
    'login.and': 'et la',
    'login.send.otp': 'Envoyer OTP',
    'login.continue.with': 'ou continuer avec',
    'login.google': 'Google',
    'login.apple': 'Apple',
    'login.email': 'Email',
    'login.features.gaming': 'Contenu de jeu',
    'login.features.mobile': 'Mobile First',
    'login.features.streaming': 'Streaming rapide',
    'login.security': 'Vos données sont cryptées et sécurisées',
    'login.footer.text': 'Nouveau sur GlobalSport ?',
    'login.footer.highlight': 'Commencez gratuitement !',
    'login.stats.users': '10M+ Utilisateurs',
    'login.stats.uptime': '99,9% Temps de fonctionnement',
    
    // OTP Modal
    'otp.title': 'Vérifiez votre téléphone',
    'otp.subtitle': 'Presque terminé ! Complétez votre vérification',
    'otp.description': 'Entrez le code à 4 chiffres envoyé à',
    'otp.features.secure': 'Connexion sécurisée',
    'otp.features.quick': 'Configuration rapide',
    'otp.features.protected': 'Compte protégé',
    'otp.security': 'Votre vérification est sécurisée et cryptée',
    'otp.resend.timer': 'Renvoyer disponible dans',
    'otp.resend.description': 'Vous n\'avez pas reçu le code ?',
    'otp.resend.button': 'Renvoyer OTP',
    'otp.verify.button': 'Vérifier et continuer',
    'otp.tips.check': 'Vérifiez vos messages pour le code',
    'otp.tips.expires': 'Le code expire dans 2 minutes',
    
    // Subscription Page
    'subscription.title': 'Choisissez votre plan',
    'subscription.subtitle': 'Débloquez les fonctionnalités premium avec nos plans flexibles',
    'subscription.daily': 'Plan quotidien',
    'subscription.weekly': 'Plan hebdomadaire',
    'subscription.monthly': 'Plan mensuel',
    'subscription.popular': 'Le plus populaire',
    'subscription.price': '₹{price}',
    'subscription.features.unlimited': 'Streaming illimité',
    'subscription.features.hd': 'Qualité HD et 4K',
    'subscription.features.devices': 'Plusieurs appareils',
    'subscription.features.offline': 'Téléchargements hors ligne',
    'subscription.features.ads': 'Expérience sans publicité',
    'subscription.features.support': 'Support prioritaire',
    'subscription.payment.title': 'Sélectionner le mode de paiement',
    'subscription.payment.wallet': 'Portefeuille',
    'subscription.payment.bank': 'Virement bancaire',
    'subscription.coupon': 'Vous avez un code promo ?',
    'subscription.coupon.placeholder': 'Entrez le code promo',
    'subscription.coupon.apply': 'Appliquer',
    'subscription.pay.button': 'Payer maintenant',
    
    // Profile Menu
    'profile.menu.profile': 'PROFIL',
    'profile.menu.subscriptions': 'ABONNEMENTS',
    'profile.menu.help': 'AIDE',
    'profile.menu.about': 'À PROPOS',
    'profile.menu.faq': 'FAQ',
    'profile.menu.logout': 'DÉCONNEXION',
    
    // Subscription Management
    'subscription.management.title': 'Gestion des abonnements',
    'subscription.management.subtitle': 'Gérez votre abonnement GlobalSport et la facturation',
    'subscription.management.back': 'Retour au profil',
    'subscription.management.plan': 'Premium Mensuel',
    'subscription.management.status': 'Actif',
    'subscription.management.start.date': 'Date de début',
    'subscription.management.next.billing': 'Prochaine facturation',
    'subscription.management.billing.cycle': 'Cycle de facturation',
    'subscription.management.auto.renewal': 'Renouvellement automatique',
    'subscription.management.upgrade': 'Mettre à niveau le plan',
    'subscription.management.cancel': 'Annuler l\'abonnement',
    'subscription.management.features.title': 'Fonctionnalités de votre plan',
    'subscription.management.usage.title': 'Statistiques d\'utilisation',
    'subscription.management.usage.devices': 'Appareils actifs',
    'subscription.management.usage.downloads': 'Téléchargements',
    'subscription.management.usage.streaming': 'Heures de streaming',
    'subscription.management.billing.title': 'Historique de facturation',
    'subscription.management.payment.title': 'Mode de paiement',
    'subscription.management.payment.update': 'Mettre à jour',
    
    // Unsubscribe Page
    'unsubscribe.title': 'Nous sommes désolés de vous voir partir !',
    'unsubscribe.subtitle': 'Aidez-nous à nous améliorer en nous disant pourquoi vous vous désabonnez',
    'unsubscribe.reason.expensive': 'Trop cher',
    'unsubscribe.reason.not.using': 'N\'utilise pas le service',
    'unsubscribe.reason.technical': 'Problèmes techniques',
    'unsubscribe.reason.alternative': 'Trouvé une meilleure alternative',
    'unsubscribe.reason.content': 'Contenu pas intéressant',
    'unsubscribe.reason.emails': 'Trop d\'emails/notifications',
    'unsubscribe.reason.temporary': 'Faire une pause (temporaire)',
    'unsubscribe.reason.other': 'Autre raison',
    'unsubscribe.feedback': 'Commentaires supplémentaires (optionnel)',
    'unsubscribe.feedback.placeholder': 'Dites-nous plus sur votre expérience ou suggestions d\'amélioration...',
    'unsubscribe.warning.title': 'Informations importantes',
    'unsubscribe.warning.billing': 'Votre abonnement se terminera à la fin de la période de facturation actuelle',
    'unsubscribe.warning.access': 'Vous conserverez l\'accès jusqu\'à ce moment-là',
    'unsubscribe.warning.refund': 'Aucun remboursement ne sera émis pour la période actuelle',
    'unsubscribe.warning.resubscribe': 'Vous pouvez vous réabonner à tout moment',
    'unsubscribe.confirm': 'Confirmer la désinscription',
    'unsubscribe.stay': 'Garder l\'abonnement',
    'unsubscribe.success.title': 'Désabonnement réussi',
    'unsubscribe.success.message': 'Nous sommes désolés de vous voir partir ! Votre abonnement a été annulé et vous ne serez plus facturé.',
    'unsubscribe.success.logout': 'Vous serez automatiquement déconnecté dans {count} secondes.',
    'unsubscribe.success.continue': 'Continuer vers l\'accueil',
    'unsubscribe.success.resubscribe': 'Se réabonner',
    
    // Common
    'common.loading': 'Chargement...',
    'common.error': 'Erreur',
    'common.success': 'Succès',
    'common.cancel': 'Annuler',
    'common.confirm': 'Confirmer',
    'common.close': 'Fermer',
    'common.back': 'Retour',
    'common.next': 'Suivant',
    'common.previous': 'Précédent',
    'common.save': 'Enregistrer',
    'common.edit': 'Modifier',
    'common.delete': 'Supprimer',
    'common.yes': 'Oui',
    'common.no': 'Non',
    
    // Homepage Content
    'homepage.carousel.title': 'Découvrez des vidéos incroyables',
    'homepage.carousel.subtitle': 'Regardez les dernières vidéos les plus populaires',
    'homepage.categories.title': 'Catégories de vidéos',
    'homepage.categories.subtitle': 'Explorez les vidéos par catégorie',
    'homepage.explore.title': 'Explorer plus',
    'homepage.explore.subtitle': 'Trouvez votre prochaine vidéo favorite',
    'homepage.videos.title': 'Vidéos en vedette',
    'homepage.videos.subtitle': 'Regardez les vidéos et avis',
    'homepage.action.play': 'Regarder maintenant',
    'homepage.action.watch': 'Regarder',
    'homepage.action.explore': 'Explorer',
    'homepage.action.moreInfo': 'Plus d\'infos',
  }
};

export const TranslationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  const t = (key: string): string => {
    const translation = translations[language][key as keyof typeof translations[typeof language]];
    if (!translation) {
      return key; // Fallback to key if translation is missing
    }
    return translation;
  };

  return (
    <TranslationContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};
